import Debug from 'debug';
import slugify from 'slugify';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AuthorizationError } from './Exceptions';
import {
  getAccountAndCheckResponse,
  filterFeedItems,
  filterFeedItemsByItemType,
  metric,
  updateFeedItemTimestamp,
  isTimeoutResponse,
  isErrorResponse,
  limitFilterStateToValidUIFilterState,
} from './util';
import {
  newFeedItem,
  connectError,
  reconnectEvent,
  reconnectFailed,
  connectEvent,
  limitFeedItems,
} from './feed';
import {
  type OrganismState,
  status,
  errors,
  type LivefireError,
  LivefireStatus,
  ViewState,
  FilterState,
  EventHandlerContext,
} from './types';
import { createNotification } from './notification';
import { allSettledPolyfill, entriesOf } from '@/functions';
import { livefireItemTypes } from '@/constants';
import { EnvironmentHelper, FeedItem, LivefireApi, MetroContext } from '@/classes';
import { LivefireEvent } from '@/classes/LivefireApi/LivefireApi';

const debug = Debug('livefire:organism:status'); // eslint-disable-line new-cap

type SetLivefireStatus = React.Dispatch<React.SetStateAction<LivefireStatus>>;

export const organismInitialState: OrganismState = {
  api: null,
  error: null,
  status: status.INITIALIZING,
  // whether .feed is at the top or scrolled position
  scrollPosition: 'top',
  // whether the first card's help tooltip is visible
  firstTooltipVisible: false,
};

type ErrorAction = { type: 'error'; payload: { error: NonNullable<OrganismState['error']> } };
type ResetAction = { type: 'reset' | 'resetError' };
export type ScrollAction = {
  type: 'scroll';
  payload: { position: OrganismState['scrollPosition'] };
};
type StatusAction = {
  type: 'status';
  payload: { status: OrganismState['status']; resetError?: boolean };
};
type ToolTipAction = { type: 'tooltip'; payload: { position: number; visible: boolean } };
export type StatusActions = ErrorAction | ResetAction | ScrollAction | StatusAction | ToolTipAction;

/** not logged in & account error are not retry-able */
const nonRetryableErrors: Readonly<LivefireError[]> = [errors.ACCOUNT_ERROR, errors.NOT_LOGGED_IN];

/**
 * useReducer() reducer for Livefire.
 */
export function organismStatusReducer(state: OrganismState, action: StatusActions): OrganismState {
  const newState = { ...state };
  switch (action.type) {
    // set an error
    case 'error':
      newState.error = action.payload.error;
      // Check if this is a retry-able error
      if (nonRetryableErrors.includes(action.payload.error)) {
        newState.status = status.ERROR;
      } else {
        newState.status = status.RETRY_ERROR;
      }
      return newState;

    // re-load the feed (not that this does not reload the account data)
    case 'reset':
      newState.error = null;
      newState.status = status.LOAD_FEED;
      return newState;

    // just reset an error
    case 'resetError':
      newState.error = null;
      return newState;

    case 'scroll':
      newState.scrollPosition = action.payload.position;
      return newState;

    // set the status
    case 'status':
      newState.status = action.payload.status;
      const resetError = action.payload.resetError ?? false;
      if (resetError) {
        newState.error = null;
      }
      return newState;

    case 'tooltip':
      if (action.payload.position === 1) {
        newState.firstTooltipVisible = action.payload.visible;
      }
      return newState;

    default:
      throw new Error('Invalid action type');
  }
}

/**
 * Add the default Api if one is not already provided.
 * @param Status object providing the api, if available.
 * @param setLivefireStatus Set method for updating the status object.
 */
export function useApi(
  { api }: LivefireStatus,
  setLivefireStatus: React.Dispatch<React.SetStateAction<LivefireStatus>>,
) {
  useEffect(() => {
    if (api) {
      return;
    }

    setLivefireStatus((state: LivefireStatus) => ({
      ...state,
      api: new LivefireApi(),
    }));
  }, [api, setLivefireStatus]); // include sets so we don't disable the rule.
}

export function useLivefireItemTypes({ api, cardTypes }: LivefireStatus) {
  useEffect(() => {
    if (!api) {
      return;
    }

    api.setCardTypes(cardTypes);
  }, [api, cardTypes]);
}

/**
 * Monitor the status of environment, token, and client ID and setup LivefireApi when ready.
 *
 * @param livefireStatus
 * @param setLivefireStatus
 */
export function useLivefireStatus(
  livefireStatus: LivefireStatus,
  setLivefireStatus: React.Dispatch<React.SetStateAction<LivefireStatus>>,
  organismState: unknown,
  dispatch: React.Dispatch<StatusActions>,
) {
  const environment = EnvironmentHelper.getEnvironment();

  useEffect(() => {
    if (!environment || !livefireStatus.authToken) {
      dispatch({ type: 'error', payload: { error: errors.NOT_LOGGED_IN } });
    } else if (environment && livefireStatus.authToken && livefireStatus.clientId) {
      // setup LivefireApi with the environment & client details
      const api = livefireStatus.api; // eslint-disable-line prefer-destructuring
      api.token = livefireStatus.authToken;
      api.clientId = livefireStatus.clientId;

      // update the application status to show we are loading the feed
      dispatch({ type: 'status', payload: { status: status.LOAD_FEED, resetError: true } });
    }
  }, [
    environment,
    livefireStatus.authToken,
    livefireStatus.clientId,
    livefireStatus.api,
    dispatch,
    setLivefireStatus,
  ]); // include sets so we don't disable the rule.
}

/**
 * Load client data and initial feed data when ready
 */
export function useFeedData(
  livefireStatus: LivefireStatus,
  setLivefireStatus: SetLivefireStatus,
  organismState: OrganismState,
  dispatch: React.Dispatch<StatusActions>,
  setFeedItems: React.Dispatch<React.SetStateAction<FeedItem[]>>,
) {
  const environment = EnvironmentHelper.getEnvironment();

  useEffect(() => {
    if (
      organismState.status === status.LOAD_FEED &&
      environment &&
      livefireStatus.authToken &&
      livefireStatus.clientId
    ) {
      try {
        debug('Loading feed');
        dispatch({ type: 'status', payload: { status: status.LOADING_FEED } });

        // don't treat failures as deal breakers...
        allSettledPolyfill([
          livefireStatus.api
            .getFeedItems()
            .then((result) => ({ ...result, method: 'getFeedItems' })),
          livefireStatus.api
            .getMutedFeedItems()
            .then((result) => ({ ...result, method: 'getMutedFeedItems' })),
        ]).then((results) => {
          debug('Received feed items', results);
          let feedItems: FeedItem[] = [];
          let feedErrors = false;
          results.forEach((result) => {
            if (
              result.status !== 'fulfilled' ||
              (result.status === 'fulfilled' && 'value' in result && result.value.ok !== true)
            ) {
              const method = 'value' in result ? result.value.method : 'unknown';
              console.error(`error retrieving feed items for method: ${method}`);
              feedErrors = true;
              return;
            }

            // add the feedItems to our list
            if ('value' in result && 'feedItems' in result.value) {
              feedItems = feedItems.concat(result.value.feedItems);
            }
          });

          if (feedErrors) {
            setFeedItems([]);
            dispatch({ type: 'error', payload: { error: errors.FEED_ERROR } });
            return;
          }

          setFeedItems(limitFeedItems(feedItems));
          dispatch({ type: 'status', payload: { status: status.LOADED, resetError: true } });
        });
      } catch (err) {
        console.error('Initial feed error', err);
        dispatch({ type: 'error', payload: { error: errors.FEED_ERROR } });
      }
    }
  }, [
    organismState.status,
    dispatch,
    livefireStatus.api,
    livefireStatus.authToken,
    livefireStatus.clientId,
    environment,
    setFeedItems,
  ]); // include sets so we don't disable the rule.
}

/**
 * Filters the feed items based on the searchTerm
 * @param viewState state of the view
 * @param setViewState State function to set the Livefire container's view state
 * @param organismState Livefire container status state
 */
export function useFeedFilter(
  viewState: ViewState,
  feedItems: FeedItem[],
  organismState: OrganismState,
  setViewState: React.Dispatch<React.SetStateAction<ViewState>>,
) {
  const { searchTerm, selectedItemTypes } = viewState;
  useEffect(() => {
    if (organismState.status === status.LOADED) {
      const itemsByItemType = searchTerm
        ? feedItems
        : filterFeedItemsByItemType(feedItems, selectedItemTypes);
      const filteredItems = filterFeedItems(itemsByItemType, searchTerm);

      setViewState((state: ViewState) => ({
        ...state,
        filteredItems,
        filtered: true,
        itemTypeFilterEmptiedList: Boolean(
          !searchTerm && feedItems.length && !itemsByItemType.length,
        ),
      }));
    }
    // include sets so we don't disable the rule.
  }, [searchTerm, feedItems, selectedItemTypes, organismState.status, setViewState]);
}

/**
 * Retrieve client details
 */
export function useAccountData(
  livefireStatus: LivefireStatus,
  setLivefireStatus: SetLivefireStatus,
  organismState: unknown,
  dispatch: React.Dispatch<StatusActions>,
) {
  const environment = EnvironmentHelper.getEnvironment();

  useEffect(() => {
    // if we do not have the client ID, make a request to retrieve that
    // TODO: do we want to make this call or do we want to pull in JWT parsing
    //   and get the ID from that?
    if (environment && livefireStatus.authToken && !livefireStatus.clientId) {
      try {
        getAccountAndCheckResponse(livefireStatus.authToken).then((result) => {
          if (isTimeoutResponse(result)) {
            console.error('Timeout retrieving account details');
            dispatch({ type: 'error', payload: { error: errors.ACCOUNT_TIMEOUT } });
            return;
          }

          if (isErrorResponse(result)) {
            // bubble up authorization errors
            if (result.error instanceof AuthorizationError) {
              throw result.error;
            }
            dispatch({ type: 'error', payload: { error: errors.ACCOUNT_ERROR } });
            return;
          }

          setLivefireStatus((state) => ({
            ...state,
            clientId: result.data.clientId,
            subscriptionId: result.data.subscriptionId,
            subscriptionStatus: result.data.subscriptionStatus,
            username: result.data.userName,
          }));
          dispatch({ type: 'resetError' });
        });
      } catch (err) {
        debug('Error retrieving account details', err);
        if (err instanceof AuthorizationError) {
          setLivefireStatus((state) => ({
            ...state,
            authToken: null,
          }));
          dispatch({ type: 'error', payload: { error: errors.NOT_LOGGED_IN } });
          return;
        }

        dispatch({
          type: 'error',
          payload: { error: errors.ACCOUNT_ERROR },
        });
      }
    }
  }, [environment, livefireStatus.authToken, livefireStatus.clientId, dispatch, setLivefireStatus]);
  // include sets so we don't disable the rule.
}

export function useFeedTimer(
  { status: lfStatus }: OrganismState,
  setFeedItems: React.Dispatch<React.SetStateAction<FeedItem[]>>,
  updateInterval: number,
) {
  useEffect(() => {
    if (lfStatus !== status.LOADED || updateInterval <= 0) {
      return undefined;
    }

    debug('card update timer set');
    const timer = setInterval(() => {
      setFeedItems((feedItems) => {
        const result = updateFeedItemTimestamp(feedItems, Date.now());
        if (!Array.isArray(result)) {
          const { items, updates } = result;
          return updates > 0 ? [...items] : feedItems;
        }
        return result;
      });
    }, updateInterval);

    return function undo() {
      debug('card update timer cleared');
      clearInterval(timer);
    };
  }, [lfStatus, setFeedItems, updateInterval]); // include sets so we don't disable the rule.
}

export function useMetricFilterOpen(viewState: ViewState) {
  const { filterButtonActive } = viewState;
  const metroContext = useContext(MetroContext);

  // We deliberately don't capture `viewState.selectedItemTypes` in our list because we want to
  // capture it once when the filter button opens. If we added it to our list, we'd fire on every
  // item change, which we're attempting to avoid.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const persistedFilterItemTypes = useMemo(() => viewState.selectedItemTypes, [filterButtonActive]);

  useEffect(() => {
    if (!filterButtonActive) {
      return;
    }

    const keys: (keyof typeof persistedFilterItemTypes)[] = [
      livefireItemTypes.click,
      livefireItemTypes.open,
      livefireItemTypes.sent,
      livefireItemTypes.videoWatched,
      livefireItemTypes.comment,
      livefireItemTypes.like,
      livefireItemTypes.replyWithVideo,
    ];

    const states: string[] = [];
    keys.forEach((key) =>
      states.push(
        `click.livefire.filter_open.${slugify(key, { replacement: '_', lower: true })}.${
          persistedFilterItemTypes[key] ? 'on' : 'off'
        }`,
      ),
    );

    metric(states, metroContext)();
  }, [filterButtonActive, metroContext, persistedFilterItemTypes]);
}

function backfillFilterState(partial: Partial<FilterState>): FilterState {
  const fullFilterState = livefireItemTypes.items.reduce(
    (carry, current) => ({ ...carry, [current]: false }),
    {} as FilterState,
  );
  return {
    ...fullFilterState,
    ...partial,
  };
}

export function useFilterPropSync(
  externalFilterState: Partial<FilterState>,
  viewState: ViewState,
  setExternalFilterState: ((state: FilterState) => void) | null,
  setViewState: React.Dispatch<React.SetStateAction<ViewState>>,
) {
  // use a ref to know when to ignore an update pass.
  const ignoreFilterStateUpdate = useRef(false);
  const ignoreViewStateUpdate = useRef(false);

  // set up our internal state in case an external state isn't provided.
  const [internalFilterState, setInternalFilterState] = useState({
    [livefireItemTypes.open]: true,
    [livefireItemTypes.click]: true,
    [livefireItemTypes.videoWatched]: true,
    [livefireItemTypes.sent]: true,
  } as FilterState);

  // determine which state setup to use based on whats provided.
  const [filterState, setFilterState] = useMemo(() => {
    if (setExternalFilterState) {
      return [externalFilterState, setExternalFilterState];
    }

    return [internalFilterState, setInternalFilterState];
  }, [externalFilterState, internalFilterState, setExternalFilterState, setInternalFilterState]);

  const { selectedItemTypes } = viewState;

  // if the internal state types change, notify the external side.
  useEffect(() => {
    if (ignoreFilterStateUpdate.current) {
      ignoreFilterStateUpdate.current = false;
      ignoreViewStateUpdate.current = false;
      return;
    }

    ignoreFilterStateUpdate.current = true;
    setFilterState(backfillFilterState({ ...selectedItemTypes }));
  }, [selectedItemTypes, setFilterState]);

  // if the external state types change, notify the internal side.
  useEffect(() => {
    if (ignoreViewStateUpdate.current) {
      ignoreFilterStateUpdate.current = false;
      ignoreViewStateUpdate.current = false;
      return;
    }

    ignoreViewStateUpdate.current = true;

    setViewState((state: ViewState) => ({
      ...state,
      selectedItemTypes: {
        ...limitFilterStateToValidUIFilterState(filterState),
      },
    }));
  }, [filterState, setViewState]);
}

export function useSearchTermPropSync(
  externalSearchTerm: string,
  viewState: ViewState,
  setExternalSearchTerm: (term: string) => void,
  setViewState: React.Dispatch<React.SetStateAction<ViewState>>,
) {
  // use a ref to know when to ignore an update pass.
  const ignoreSearchTermUpdate = useRef('');
  const ignoreViewStateUpdate = useRef('');

  // set up our internal state in case an external state isn't provided.
  const [internalSearchTerm, setInternalSearchTerm] = useState('');

  // determine which state setup to use based on whats provided.
  const [searchTerm, setSearchTerm] = useMemo(() => {
    if (setExternalSearchTerm) {
      return [externalSearchTerm, setExternalSearchTerm];
    }

    return [internalSearchTerm, setInternalSearchTerm];
  }, [externalSearchTerm, internalSearchTerm, setExternalSearchTerm, setInternalSearchTerm]);

  const viewStateSearchTerm = viewState.searchTerm;

  // if the internal state types change, notify the external side.
  useEffect(() => {
    if (ignoreSearchTermUpdate.current === viewStateSearchTerm) {
      ignoreSearchTermUpdate.current = '';
      return;
    }

    ignoreSearchTermUpdate.current = viewStateSearchTerm;
    setSearchTerm(viewStateSearchTerm);
  }, [viewStateSearchTerm, setSearchTerm]);

  // if the external state types change, notify the internal side.
  useEffect(() => {
    if (ignoreViewStateUpdate.current === searchTerm) {
      ignoreViewStateUpdate.current = '';
      return;
    }

    ignoreViewStateUpdate.current = searchTerm;
    setViewState((state) => ({
      ...state,
      searchTerm,
    }));
  }, [searchTerm, setViewState]);
}

/**
 * Configure and connect the the Livefire API to the live background source.
 */
export function useSocket(
  { api }: LivefireStatus,
  { status: lfStatus }: OrganismState,
  setFeedItems: React.Dispatch<React.SetStateAction<FeedItem[]>>,
  setNotification: React.Dispatch<React.SetStateAction<ReturnType<typeof createNotification>>>,
  localCreateNotification: typeof createNotification,
) {
  useEffect(() => {
    if (!api) {
      return undefined;
    }

    const context: EventHandlerContext = {
      api,
      createNotification: localCreateNotification,
      setFeedItems,
      setNotification,
    };
    const newFeedItemEventHandler = newFeedItem.bind(null, context);
    const connectionErrorEventHandler = connectError.bind(null, context);
    const connectionEventHandler = connectEvent.bind(null, context);
    const reconnectEventHandler = reconnectEvent.bind(null, context);
    const reconnectFailedEventHandler = reconnectFailed.bind(null, context);
    const eventHandlers = {
      /* eslint-disable camelcase */
      connect_error: connectionErrorEventHandler,
      connect_timeout: connectionErrorEventHandler,
      connect: connectionEventHandler,
      newFeedItem: newFeedItemEventHandler,
      reconnect: reconnectEventHandler,
      reconnect_failed: reconnectFailedEventHandler,
      /* eslint-enable camelcase */
    } as Record<LivefireEvent, Parameters<LivefireApi['addEventListener']>[1]>;

    if (lfStatus === status.LOADED) {
      debug('registering new feed item listener and requesting connect');
      entriesOf(eventHandlers).forEach(([event, handler]) => {
        api.addEventListener(event, handler);
      });
      api.connect();
    }

    return () => {
      debug('requesting disconnect and removing new feed item listener');
      api.disconnect();
      entriesOf(eventHandlers).forEach(([event, handler]) => {
        api.removeEventListener(event, handler);
      });
    };
    // include sets so we don't disable the rule.
  }, [api, lfStatus, localCreateNotification, setFeedItems, setNotification]);
}
